import React, { useState, useEffect, useRef } from "react";
import { FaPaperPlane, FaCommentDots, FaTimes, FaReply } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import io from "socket.io-client";
import dayjs from "dayjs";

// Initialize socket connection (replace URL with your backend's socket URL)
const token = localStorage.getItem("jwt_token");
const socket = io("https://adey.lucky.et", {
  path: "/socket.io",
  transports: ["websocket"],
  withCredentials: true,
  auth: {
    token: token
  }
});

const FloatingContactButton = ({ userId = "mockUser123", lang = "en" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // Base URL for contact images - replace with your actual base URL if different
  const CONTACT_IMAGE_BASE_URL = "https://adey.lucky.et/uploads/contact_images/";

  const translations = {
    en: {
      title: "Contact Us",
      subject: "Subject",
      message: "Message",
      send: "Send",
      success: "Message sent successfully!",
      error: "Failed to send message.",
      placeholderMessage: "Type your message...",
      attachImage: "Attach image",
      noMessages: "No messages yet. Start a new conversation!",
      replyingTo: "Replying to",
      cancelReply: "Cancel reply",
      you: "You",
      admin: "Admin",
      defaultSubject: "New Conversation",
    },
    am: {
      title: "ያግኙን",
      subject: "ርዕስ",
      message: "መልእክት",
      send: "ላክ",
      success: "መልእክት ተልኳል!",
      error: "መልእክት መላክ አልተሳካም።",
      placeholderMessage: "መልእክትዎን ይቀይሩ...",
      attachImage: "ምስል ያካትቱ",
      noMessages: "መልእክቶች አልተገኙም። አዲስ ውይይት ይጀምሩ!",
      replyingTo: "እዚህ እየተመለሰ",
      cancelReply: "መመለስ አስቀምጥ",
      you: "እርስዎ",
      admin: "አስተዳዳሪ",
      defaultSubject: "አዲስ ውይይት",
    },
    ti: {
      title: "ንኽብርኩም",
      subject: "ርእሲ",
      message: "መልእኽቲ",
      send: "ሰንደይ",
      success: "መልእኽቲ ተልኳም!",
      error: "መልእኽቲ ምልክት ኣይተሳነሰን።",
      placeholderMessage: "መልእኽቲካ ንሓደሽ ኣስተኻኽል...",
      attachImage: "ስእሊ ኣቕምጥ",
      noMessages: "መልእኽቲ ኣይኮነን። ሓድሽ ምይይጥ ጀምር!",
      replyingTo: "ኣብ መልሲ እዩ",
      cancelReply: "መመለስ ኣቐምጥ",
      you: "ንስኻ",
      admin: "ኣድሚን",
      defaultSubject: "ሓድሽ ምይይጥ",
    },
    or: {
      title: "Nu quunnamaa",
      subject: "Mata duree",
      message: "Ergaa",
      send: "Ergi",
      success: "Ergaan siif ergameera!",
      error: "Ergaan erguun hin milkoofne.",
      placeholderMessage: "Ergaa kee barreessi...",
      attachImage: "Suuraa fe'i",
      noMessages: "Ergaan hin jiru. Haasaa haaraa jalqabi!",
      replyingTo: "Deebii itti kennuu",
      cancelReply: "Deebii haqaa",
      you: "Ati",
      admin: "Admin",
      defaultSubject: "Haasaa Haaraa",
    },
    wo: {
      title: "Hawa wone",
      subject: "Maro",
      message: "Lakku",
      send: "Ilmelo",
      success: "Lakku kisho ganta gite!",
      error: "Lakku kisho wode hawete.",
      placeholderMessage: "Lakku kisho ganta gite...",
      attachImage: "Sawuri astito",
      noMessages: "Lakku kisho haweta. Lakku kisho hawa!",
      replyingTo: "Nu dee lëkk",
      cancelReply: "Nu weeru lëkk",
      you: "Yow",
      admin: "Admin",
      defaultSubject: "Lakku Kisho Hawa",
    },
  };

  const t = translations[lang] || translations.en;

  const togglePanel = () => setIsOpen(!isOpen);

  const fetchChatHistory = async () => {
    if (!userId) return;
    try {
      const response = await axiosInstance.get(`/contact/history/${userId}`);
      if (response.data.success) {
        setChatHistory(response.data.messages || []);
      } else {
        setChatHistory([]);
      }
    } catch (error) {
      console.error("Failed to fetch chat history:", error);
      setChatHistory([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchChatHistory();
    }
  }, [isOpen, userId]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  // Listen to new message event from backend (socket)
  useEffect(() => {
    socket.on("newContactMessage", (newMessage) => {
      console.log("Socket.IO received:", newMessage);
      console.log("Frontend userId:", userId);

      if (newMessage.user_id === userId) {
        console.log("UserId match! Processing message.");
        setChatHistory((prev) => {
          let updatedPrev = prev.map(msg => {
            if (msg.is_optimistic && msg.message === newMessage.message && msg.user_id === newMessage.user_id) {
              console.log("Replacing optimistic message with real message:", msg.id, "->", newMessage.id);
              return {
                ...newMessage,
                is_optimistic: false
              };
            }
            return msg;
          });

          const isAlreadyPermanent = updatedPrev.some(msg => msg.id === newMessage.id && !msg.is_optimistic);
          if (isAlreadyPermanent) {
            console.log("Message with this permanent ID already exists. Skipping:", newMessage.id);
            return updatedPrev;
          }

          if (newMessage.message_id) {
            console.log("Processing reply:", newMessage);
            const finalHistory = updatedPrev.map(msg => {
              if (msg.id === newMessage.message_id) {
                return {
                  ...msg,
                  replies: [...(msg.replies || []), {
                    id: newMessage.id,
                    message_id: newMessage.message_id,
                    user_id: newMessage.user_id,
                    message: newMessage.message,
                    image: newMessage.image,
                    created_at: newMessage.created_at,
                    is_admin_reply: newMessage.is_admin_reply
                  }]
                };
              }
              return msg;
            });
            console.log("Chat history updated with new reply:", JSON.stringify(finalHistory, null, 2));
            return finalHistory;
          } else {
            if (!updatedPrev.some(msg => msg.id === newMessage.id)) {
                console.log("Adding new main message from user (via socket):", newMessage);
                const finalHistory = [...updatedPrev, {
                    id: newMessage.id,
                    user_id: newMessage.user_id,
                    subject: newMessage.subject,
                    message: newMessage.message,
                    image_path: newMessage.image_path,
                    created_at: newMessage.created_at,
                    is_admin_reply: newMessage.is_admin_reply,
                    replies: newMessage.replies || []
                }];
                console.log("Chat history updated with new user message:", JSON.stringify(finalHistory, null, 2));
                return finalHistory;
            }
            return updatedPrev;
          }
        });
      } else {
        console.log("UserId mismatch. Not processing message for this user.");
      }
    });
    return () => {
      socket.off("newContactMessage");
    };
  }, [userId]);

  useEffect(() => {
    return () => {
      if (imageFile) URL.revokeObjectURL(imageFile.preview);
    };
  }, [imageFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      file.preview = URL.createObjectURL(file);
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.warning("Message cannot be empty.");
      return;
    }

    setIsSubmitting(true);

    const tempMessageId = `temp_msg_${Date.now()}`;
    const optimisticMessage = {
      id: tempMessageId,
      user_id: userId,
      subject: (!replyTo && chatHistory.length === 0) ? t.defaultSubject : null,
      message: message,
      image_path: imageFile ? imageFile.preview : null,
      created_at: new Date().toISOString(),
      is_admin_reply: false,
      replies: [],
      is_optimistic: true
    };

    setChatHistory((prev) => [...prev, optimisticMessage]);

    setMessage("");
    setImageFile(null);
    setReplyTo(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (messageInputRef.current) {
      messageInputRef.current.style.height = '48px';
    }
    
    try {
      const payload = new FormData();
      payload.append("user_id", userId);
      
      if (!replyTo && chatHistory.length === 0) {
        payload.append("subject", t.defaultSubject);
      }

      payload.append("message", message);
      if (replyTo) {
        payload.append("parent_message_id", replyTo);
      }
      if (imageFile) payload.append("image", imageFile);

      const response = await axiosInstance.post("/contact/send", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success(t.success);
      } else {
        toast.error(response.data.message || t.error);
        setChatHistory((prev) => prev.filter(msg => msg.id !== tempMessageId));
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast.error(error.response?.data?.message || error.message || t.error);
      setChatHistory((prev) => prev.filter(msg => msg.id !== tempMessageId));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUserMessage = (msg) => !msg.is_admin_reply;

  return (
    <div className="fixed bottom-6 right-6 z-50 font-inter antialiased">
      <button
        onClick={togglePanel}
        className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
        aria-label={isOpen ? "Close Contact Form" : "Open Contact Form"}
      >
        {isOpen ? <FaTimes size={24} /> : <FaCommentDots size={24} />}
      </button>

      {isOpen && (
        <div className="w-80 md:w-96 max-h-[85vh] bg-white shadow-xl rounded-lg mt-4 overflow-hidden flex flex-col border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 p-4 border-b border-gray-200 bg-blue-600 text-white rounded-t-lg">
            {t.title}
          </h2>

          <div
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-blue-50 to-indigo-50"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#a0aec0 #edf2f7" }}
          >
            {chatHistory.length === 0 ? (
              <p className="text-gray-500 text-center text-sm italic py-4">{t.noMessages}</p>
            ) : (
              chatHistory.map((msg) => (
                <div key={msg.id} className={`flex ${isUserMessage(msg) ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] p-3 rounded-2xl shadow-sm ${isUserMessage(msg) ? 'bg-blue-500 text-white rounded-br-none' : 'bg-gray-200 text-gray-900 rounded-bl-none'} ${msg.is_optimistic ? 'opacity-70' : ''}`}>
                    {msg.subject && <p className="text-sm font-semibold mb-1">{msg.subject}</p>}
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    {(msg.image_path || msg.image) && (
                      <img
                        // Corrected image source logic: use blob URL directly for optimistic updates,
                        // otherwise construct from base URL and filename.
                        src={
                          (msg.is_optimistic && (msg.image_path || msg.image).startsWith('blob:'))
                            ? (msg.image_path || msg.image)
                            : `${CONTACT_IMAGE_BASE_URL}${msg.image_path || msg.image}`
                        }
                        alt="Attachment"
                        className="mt-2 rounded-lg max-w-full h-auto object-cover border"
                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/150x100/E0E0E0/808080?text=Image+Error"; }}
                      />
                    )}
                    <div className={`text-xs mt-1 ${isUserMessage(msg) ? 'text-blue-100' : 'text-gray-600'} text-right`}>
                      {dayjs(msg.created_at).format("h:mm A, MMM D")}
                    </div>

                    {Array.isArray(msg.replies) && msg.replies.length > 0 && (
                      <div className="mt-3 space-y-2 border-l-2 border-gray-300 pl-3">
                        {msg.replies.sort((a, b) => dayjs(a.created_at).diff(dayjs(b.created_at))).map((rep) => (
                          <div key={rep.id} className={`flex ${isUserMessage(rep) ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[90%] p-3 rounded-2xl shadow-sm ${isUserMessage(rep) ? 'bg-blue-100 text-gray-800 rounded-br-none' : 'bg-blue-100 text-gray-800 rounded-bl-none'} ${rep.is_optimistic ? 'opacity-70' : ''}`}>
                              <p className="text-sm whitespace-pre-wrap">{rep.message}</p>
                              {rep.image && (
                                <img
                                  // Corrected image source logic for replies
                                  src={
                                    (rep.is_optimistic && rep.image.startsWith('blob:'))
                                      ? rep.image
                                      : `${CONTACT_IMAGE_BASE_URL}${rep.image}`
                                  }
                                  alt="Reply Attachment"
                                  className="mt-2 rounded-lg max-w-full h-auto object-cover border border-blue-200"
                                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/150x100/A7D9F7/000000?text=Image+Error"; }}
                                />
                              )}
                              <p className="text-xs text-gray-600 mt-1 text-right">
                                {dayjs(rep.created_at).format("h:mm A, MMM D")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {replyTo && (
            <div className="p-2 bg-yellow-100 text-yellow-900 rounded-md mx-4 mb-2 flex justify-between items-center text-sm">
              <span>{t.replyingTo}: {chatHistory.find(m => m.id === replyTo)?.subject || chatHistory.find(m => m.id === replyTo)?.message.substring(0, 20) + '...' || ''}</span>
              <button
                onClick={() => setReplyTo(null)}
                className="text-yellow-900 hover:text-red-600 transition-colors duration-200 p-1 rounded-full"
                title={t.cancelReply}
                aria-label={t.cancelReply}
              >
                <FaTimes size={16} />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white flex flex-col space-y-3 rounded-b-lg">
            <div className="flex items-end space-x-2">
              <textarea
                ref={messageInputRef}
                rows={1}
                placeholder={t.placeholderMessage}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="flex-1 border border-gray-300 rounded-full py-3 px-5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 ease-in-out overflow-hidden max-h-32"
                style={{ minHeight: '48px' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = (e.target.scrollHeight) + 'px';
                }}
                required
              />
              <label htmlFor="player-file-upload" className="cursor-pointer p-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-blue-600 transition-colors duration-200 flex-shrink-0">
                <FaReply className="w-6 h-6" />
                <input
                  id="player-file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  ref={fileInputRef}
                />
              </label>
              <button
                type="submit"
                disabled={isSubmitting}
                className="p-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 flex-shrink-0"
              >
                {isSubmitting ? (
                  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <FaPaperPlane className="w-6 h-6" />
                )}
              </button>
            </div>
            {imageFile && (
              <div className="flex items-center space-x-2 mt-2">
                <img
                  src={imageFile.preview}
                  alt="Image preview"
                  className="max-h-24 rounded-lg border border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default FloatingContactButton;
